import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { Button } from "../../../components/common";
import { Text } from "../../../components/common/text/text";
import EmptyState from "../../../components/home-components/empty-state";
import FilterFormV2 from "../../../components/home-components/filter-form-v2/filter-form";
import Header from "../../../components/home-components/header/header";
import Slider from "../../../components/home-components/slider";
import Table from "../../../components/home-components/table";
import TableFilter, {
  FilterOption,
} from "../../../components/home-components/table/components/filter";
import { HeaderItem } from "../../../components/home-components/table/components/header";
import {
  DataCell,
  InfoTableContainer,
  TableRow,
} from "../../../components/home-components/table/index.styled";
import {
  getCandidateHireModel,
  getCandidateHireTypes,
  getCandidateSalary,
} from "../../../components/home-components/table/utils/table.utils";
import Modal from "../../../components/modal/modal";
import TalentV2 from "../../../components/talent-v2/talent";
import { useModal, useQuery } from "../../../hooks";
import useLocalStorage from "../../../hooks/use-local-storage";
import { useTourContext } from "../../../hooks/user-tour";
import { CandidateSimple } from "../../../models/candidate.model";
import HomeDashboardPageQuery from "../../../models/home-dashboard-page-query.model";
import { AppState } from "../../../store/app-state";
import {
  clearCandidateDetail,
  clearCandidates,
  fetchCandidateOfRanking,
} from "../../../store/candidate/candidate-actions";
import { fetchCompanyPlan } from "../../../store/company/company-actions";
import {
  fetchEventData,
  fetchEventTracks,
  fetchTrackCandidates,
} from "../../../store/event/event-actions";
import {
  fetchSetInvisible,
  fetchSetVisible,
} from "../../../store/load/load-actions";
import { getCurrentPlan } from "../../../store/plan/plan-actions";
import { initialTour } from "../../../store/tours";
import { queryLoaded } from "../../../utils/query.utils";
import { ViewDesktopFilters } from "../selected-candidates/selected-candidates-page.styled";
import {
  Buttons,
  Container,
  Content,
  FilterContainer,
  Fit,
  IconFit,
  TalentPoolContainer,
  ValueFit,
} from "./talent-pool-page.styled";

const eventSlug: string = "talent-match";
const modalTalent = document.getElementById("modal-talent");

const TalentPoolPageV2: React.FC = () => {
  const { isShown, open, close } = useModal();
  const theme = useTheme();
  const { t } = useTranslation("private-home-page");
  const dispatch = useDispatch();
  const user = useSelector((state: AppState) => state.authState.user);
  const [query, setQuery] = useQuery<HomeDashboardPageQuery>();
  const { candidateRankingData } = useSelector(
    (state: AppState) => state.candidateState
  );
  const { currentEventTrackData } = useSelector(
    (state: AppState) => state.eventState
  );
  const [showModalOnboarding, setShowModalOnboarding] = useState(false);
  const [isJobOfferOpen, setIsJobOfferOpen] = useState<boolean>(false);
  const [onboarding, setOnboarding] = useLocalStorage("onboarding", false);
  const { handleSetStep } = useTourContext();

  useEffect(() => {
    dispatch(fetchEventData(eventSlug));
    dispatch(fetchEventTracks(eventSlug));
    dispatch(fetchCompanyPlan());
    dispatch(getCurrentPlan());
  }, [dispatch]);

  useEffect(() => {
    if (!candidateRankingData.loading) {
      dispatch(fetchSetInvisible());
    }
  }, [candidateRankingData.loading, dispatch]);

  const handleSearch = useCallback(
    (query: HomeDashboardPageQuery) => {
      const newFilters = { ...query };
      delete newFilters.tour;
      delete newFilters.onboarding;
      delete newFilters.forceTour;
      if (newFilters.ecosystem) {
        const lastFilters = { ...query };
        delete lastFilters.tour;
        delete lastFilters.onboarding;
        delete lastFilters.forceTour;
        if (query.ecosystem) {
          dispatch(fetchCandidateOfRanking(query.ecosystem, query));
          dispatch(fetchSetVisible());
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const loaded = queryLoaded(query);
    if (
      !candidateRankingData.data &&
      query.ecosystem &&
      !candidateRankingData.loading &&
      !candidateRankingData.error
    ) {
      handleSearch(query);
    } else if (!query.currency && user && loaded) {
      query.currency = user?.corporate?.currency || "BRL";
      setQuery(query);
    }
  }, [dispatch, query, candidateRankingData, handleSearch, setQuery, user]);

  useEffect(() => {
    if (
      query.onboarding &&
      query.onboarding === "true" &&
      !onboarding &&
      candidateRankingData.data
    ) {
      setShowModalOnboarding(true);
      setQuery({ ...query, onboarding: "false" });
    }
  }, [query, onboarding, setOnboarding, setQuery, candidateRankingData.data]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ecoParam = Object.keys(q).length;
    if (ecoParam === 0) {
      dispatch(clearCandidates());
    }
  }, [dispatch]);

  const tableHeaderItems: HeaderItem[] = [
    { value: "position", width: "2%" },
    { value: "score", width: "4%", info: "scoreInfo" },
    { value: "fit", width: "4%", info: "fitInfo" },
    { value: "name", width: "2%" },
    { value: "experience", width: "10%" },
    { value: "wage-claim", width: "15%" },
    { value: "work-model", width: "15%" },
    { value: "hire-types", width: "15%" },
  ];

  const handleOpen = useCallback(() => {
    open();
  }, [open]);

  const handleClose = () => {
    if (currentEventTrackData.data?.register) {
      dispatch(fetchTrackCandidates(currentEventTrackData.data.uuid, query));
    }
    dispatch(clearCandidateDetail());
    close();
  };

  const { candidateDetailData } = useSelector(
    (state: AppState) => state.candidateState
  );

  const [index, setIndex] = useState<number>(-1);

  const candidate = useMemo((): CandidateSimple | undefined => {
    return index >= 0 ? candidateRankingData.data?.[index] : undefined;
  }, [candidateRankingData, index]);

  // função desabilitade pois o backend deveria filtrar pessoas já contratadas, tanto para listagem quanto para o detalhe
  // const candidateStatus = useCandidateStatus({
  //   candidateId: candidate?.id || "",
  // });

  // desativando função até voltar o parse
  // const getOriginalWageClaim = useCallback(
  //   (candidate: Candidate) => {
  //     return candidate?.wage_claims?.[0]?.brl
  //       ? t("wage-claim-converted-original", {
  //           originalValue: parseFloat(
  //             candidate.wage_claims[0].brl
  //           ).toLocaleString(),
  //         })
  //       : t("wage-claim-converted");
  //   },
  //   [t]
  // );

  //função desabilitade pois o backend deveria filtrar pessoas já contratadas, tanto para listagem quanto para o detalhe
  // const handleModal = useCallback(
  //   (candidate: Candidate, candidateStatus) => {
  //     (!candidateStatus?.hired ||
  //       candidateStatus?.hired === user?.corporate.id ||
  //       candidate.company_hired === user?.corporate.id) &&
  //       handleOpen();
  //   },
  //   [handleOpen, user]
  // );

  const closeOnboardingModal = useCallback(() => {
    setShowModalOnboarding(false);
    if (candidateRankingData.data && candidateRankingData.data.length > 0) {
      const action = () => {
        setIndex(0);
        candidateRankingData.data && handleOpen();
      };

      const closeAction = () => {
        close();
      };

      handleSetStep(initialTour(action, t, closeAction));
      setOnboarding(true);
      let q = { ...query };
      delete q.forceTour;
      setQuery({ ...q, tour: "true" });
    }
  }, [
    candidateRankingData,
    setOnboarding,
    handleOpen,
    handleSetStep,
    setQuery,
    query,
    t,
    close,
  ]);

  useEffect(() => {
    if (
      query.forceTour &&
      query.forceTour === "true" &&
      candidateRankingData.data &&
      candidateRankingData.data.length > 0 &&
      !candidateRankingData.loading
    ) {
      closeOnboardingModal();
    }
  }, [query, closeOnboardingModal, candidateRankingData]);

  const PAGE_SIZE = useMemo(() => {
    return candidateRankingData.data?.length || 0;
  }, [candidateRankingData]);

  const getFireIcon = useCallback((fit: number): string => {
    const ranges = [0, 20, 40, 60, 80, 100];
    let choice = 0;

    if (fit > -1 || fit <= 100)
      choice = ranges[Math.min(Math.floor(fit / 20), 5)];

    return `https://hermes.dio.me/assets/fit/fire-${choice}.svg`;
  }, []);

  const orderOptions: FilterOption[] = [
    {
      value: "scoreDesc",
      action: () => {
        setQuery({ ...query, sortBy: "score" });
        handleSearch({ ...query, sortBy: "score" });
      },
    },
    {
      value: "fitDesc",
      action: () => {
        setQuery({ ...query, sortBy: "fit" });
        handleSearch({ ...query, sortBy: "fit" });
      },
    },
  ];

  return (
    <TalentPoolContainer>
      <Modal
        width={500}
        isShown={showModalOnboarding}
        toggle={closeOnboardingModal}
        target={modalTalent}
        handleClose={closeOnboardingModal}
        background={"#F3F3F3"}
        top={"calc(50% - 121px)"}
      >
        <Text
          as="p"
          fontWeight={800}
          color={theme.colors.gray900.main}
          align="center"
        >
          {t("onboarding.modal.title")}
        </Text>
        <Text as="p" color={theme.colors.gray900.main} align="center">
          {t("onboarding.modal.text")}
        </Text>
        <Buttons>
          <Button
            backgroundColor={theme.colors.primaryPurple.main}
            textColor={theme.colors.primaryPurple.text}
            onClick={closeOnboardingModal}
          >
            {t("onboarding.modal.button")}
          </Button>
        </Buttons>
      </Modal>
      <Modal
        width={1000}
        isShown={isShown}
        toggle={handleOpen}
        target={modalTalent}
        handleClose={handleClose}
        background={"#F3F3F3"}
        closeOnEsc={true}
      >
        <Slider
          onNext={() => setIndex(index + 1)}
          onPrev={() => setIndex(index - 1)}
          showNext={index !== PAGE_SIZE - 1 && !candidateDetailData.loading}
          showPrev={index !== 0 && !candidateDetailData.loading}
          disableKeyboard={isJobOfferOpen}
        >
          <TalentV2
            talentId={candidate?.id ?? ""}
            ecosystem={query.ecosystem || ""}
            ecosystemTech={""}
            currencyIso={query.currency || ""}
            setIsJobOfferOpen={setIsJobOfferOpen}
            publicId={candidate?.id || ""}
          />
        </Slider>
      </Modal>
      <Container>
        <Header
          title={t("private-home-page:tabs.talentPool.title")}
          subtitle={user?.corporate?.name}
          isSelecteds={true}
        />

        <ViewDesktopFilters id="filter">
          <FilterFormV2
            initialMaxSalary={50000}
            isCandidatePool={true}
            searchCallback={(query) => {
              handleSearch(query);
            }}
            disableKeyboard={isShown}
          />
        </ViewDesktopFilters>
        <Content id="result">
          {candidateRankingData.data && candidateRankingData.data.length > 0 && (
            <>
              <InfoTableContainer>
                <Text
                  fontWeight={600}
                  fontSize={16}
                  margin="0"
                  color={theme.colors.primaryBlue.main}
                >
                  {t("ranking-table.showing-total-talent", {
                    count: candidateRankingData.data?.length || 0,
                  })}
                </Text>
                <FilterContainer>
                  <TableFilter
                    label={"order-by"}
                    options={orderOptions}
                    initial={
                      query?.sortBy === "fit"
                        ? orderOptions[1]
                        : orderOptions[0]
                    }
                  />
                </FilterContainer>
              </InfoTableContainer>

              <Table headerItems={tableHeaderItems}>
                {candidateRankingData.data?.map((c, key) => (
                  <TableRow
                    key={key}
                    onClick={() => {
                      setIndex(key);
                      handleOpen();
                    }}
                    id={`candidate-${key}`}
                  >
                    <DataCell>{key + 1}º</DataCell>
                    <DataCell>{c.score}</DataCell>
                    <DataCell>
                      <Fit>
                        <IconFit src={getFireIcon(c.fit)} />
                        <ValueFit>{c.fit}%</ValueFit>
                      </Fit>
                    </DataCell>
                    <DataCell color={theme.colors.primaryBlue.main} bold={600}>
                      {c.initials_name.replace(" ", "").toUpperCase()}
                    </DataCell>
                    <DataCell>{c.experience_time_tech}</DataCell>
                    <DataCell>{getCandidateSalary(c, t)}</DataCell>
                    <DataCell>{getCandidateHireModel(c, t)}</DataCell>
                    <DataCell>{getCandidateHireTypes(c)}</DataCell>
                  </TableRow>
                ))}
              </Table>
            </>
          )}
          {candidateRankingData.data &&
            candidateRankingData.data.length === 0 &&
            Object.keys(query).length > 1 &&
            !candidateRankingData.loading && (
              <EmptyState
                title={t("ranking-table.empty-state.title")}
                description={t("ranking-table.empty-state.description")}
              />
            )}
        </Content>
      </Container>
    </TalentPoolContainer>
  );
};

export default TalentPoolPageV2;
